import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { EntitlementsService } from 'app/core/entitlements/entitlements.service';
import { FeatureKey } from 'app/core/entitlements/entitlements.types';
import { filter, map, startWith } from 'rxjs';

/**
 * A whole screen the plan does not reach.
 *
 * The screen renders behind it, greyed: an operator should see what they are
 * missing, not a blank page or a redirect that looks like a broken link. The
 * card cannot be dismissed into the screen, though — closing it goes back to
 * the dashboard, because "look but do not touch" has to mean it.
 *
 * Which screens are gated is declared on the route, as `data.requiresFeature`.
 */
@Component({
    selector: 'app-locked-screen',
    standalone: true,
    imports: [MatButtonModule, MatIconModule, TranslocoModule],
    // The layout it sits in is a flex row. As an ordinary element this
    // component became a flex item and took a share of the width from the
    // screen itself, which is how half the page turned to empty grey.
    styles: [':host { display: contents; }'],
    template: `
        @if (locked(); as feature) {
            <div *transloco="let t"
                 class="fixed inset-0 z-99 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[1px]">
                <div class="flex w-full max-w-md flex-col items-start gap-4 rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800">
                    <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-900/30">
                        <mat-icon class="icon-size-6 text-amber-700 dark:text-amber-500"
                                  [svgIcon]="'heroicons_solid:lock-closed'" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <h2 class="m-0 text-2xl font-extrabold tracking-tight">{{ t('locked_screen_title') }}</h2>
                        <p class="m-0 text-secondary">{{ t('locked_' + feature) }}</p>
                    </div>
                    <div class="mt-2 flex w-full flex-col gap-3 sm:flex-row">
                        <button mat-flat-button color="primary" class="rounded-xl" (click)="seePlans()">
                            {{ t('see_plans') }}
                        </button>
                        <button mat-stroked-button class="rounded-xl" (click)="leave()">
                            {{ t('back_to_dashboard') }}
                        </button>
                    </div>
                </div>
            </div>
        }
    `,
})
export class LockedScreenComponent {
    private readonly _router = inject(Router);
    private readonly _route = inject(ActivatedRoute);
    private readonly _entitlements = inject(EntitlementsService);

    /** Recomputed on every navigation, since the route carries the answer. */
    private readonly _url = toSignal(
        this._router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            map(() => this._router.url),
            startWith(this._router.url)
        ),
        { initialValue: this._router.url }
    );

    readonly locked = computed<FeatureKey | null>(() => {
        this._url();

        let route = this._route.snapshot.root;

        while (route.firstChild) {
            route = route.firstChild;
        }

        const feature = route.data?.['requiresFeature'] as FeatureKey | undefined;

        if (!feature) {
            return null;
        }

        return this._entitlements.allows(feature) ? null : feature;
    });

    seePlans(): void {
        this._router.navigate(['/settings/plan-billing']);
    }

    leave(): void {
        this._router.navigate(['/dashboard']);
    }
}
